import axiosInstance, { coreBackendAxios } from '../store/axiosInterceptor';

export interface UpdateProfilePayload {
  name?: string;
  email?: string;
  phone?: string;
  location?: string;
  avatar?: string;
}

export interface ProfileUpdateResponse {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
  location?: string;
  avatar?: string;
  role?: string;
}

export interface NotificationSettings {
  email_enabled?: boolean;
  email_match_alert?: boolean;
  email_upcoming_session_reminder?: boolean;
  email_session_cancellation?: boolean;
  email_rescheduling_request?: boolean;
  email_platform_announcement?: boolean;
  email_new_message?: boolean;
  in_app_enabled?: boolean;
  in_app_upcoming_session_reminder?: boolean;
  in_app_session_changes?: boolean;
  in_app_new_student_assigned?: boolean;
  in_app_new_message?: boolean;
  in_app_action_required?: boolean;
  in_app_attendance_update?: boolean;
  in_app_platform_update?: boolean;
  ai_auto_approval_enabled?: boolean;
}

class ProfileService {
  /**
   * Update the admin user's profile
   * Uses PUT /api/users/:id endpoint (admin profile update)
   */
  static async updateAdminProfile(userId: string, data: UpdateProfilePayload): Promise<ProfileUpdateResponse> {
    try {
      const response = await axiosInstance.put(`/api/users/${userId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  /**
   * Update the coach's profile
   * Uses PUT /api/coaches/me endpoint for coach-specific updates
   */
  static async updateCoachProfile(data: UpdateProfilePayload): Promise<ProfileUpdateResponse> {
    try {
      const response = await axiosInstance.put('/api/coaches/me/profile', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update coach profile');
    }
  }

  /**
   * Update user profile by ID
   * Uses PUT /api/users/:id endpoint
   */
  static async updateUserProfile(userId: string, data: UpdateProfilePayload): Promise<ProfileUpdateResponse> {
    try {
      const response = await axiosInstance.put(`/api/users/${userId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update user profile');
    }
  }

  /**
   * Upload profile picture and return image URL
   */
  static async uploadProfilePicture(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await coreBackendAxios.post('/core-backend/v1/api/v1/storage/profile/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const payload = response.data?.response ?? response.data ?? {};
      const urls = Array.isArray(payload.urls)
        ? payload.urls.filter((url: unknown) => typeof url === 'string')
        : [];
      const fallbackUrl =
        (typeof payload.url === 'string' && payload.url) ||
        (typeof payload.fileUrl === 'string' && payload.fileUrl) ||
        (typeof payload.file_url === 'string' && payload.file_url) ||
        '';
      const fileUrl = urls.length ? urls[urls.length - 1] : fallbackUrl;
      if (!fileUrl) {
        throw new Error('Upload failed: missing profile image URL.');
      }

      return fileUrl;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload profile picture');
    }
  }

  /**
   * Get notification settings for the authenticated user
   */
  static async getNotificationSettings(): Promise<NotificationSettings> {
    try {
      const response = await axiosInstance.get('/api/notifications/settings');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch notification settings');
    }
  }

  /**
   * Update notification settings for the authenticated user
   */
  static async updateNotificationSettings(settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    try {
      const response = await axiosInstance.put('/api/notifications/settings', settings);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update notification settings');
    }
  }
}

export default ProfileService;
